import { useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { isApiError } from '@/api'
import { EnterpriseRole } from '@/api/generated'
import { updateMemberRole } from '@/api/members'
import { useToast } from '@/components/Toast/ToastContext'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ROLE_LABELS } from '@/constants/enterprise'
import { useAnalytics } from '@/hooks/useAnalytics'
import { MY_ACTIVE_COST_CENTER_QUERY_KEY } from '@/pages/app/Team/Department/utils'
import { selectUser } from '@/store/features/user/userSlice'
import { EventName } from '@/utils/eventDefinitions'

// API-allowed values for PATCH /members/{user_id}, mirrors
// ASSIGNABLE_INVITE_ROLE_VALUES in the backend (member, admin, it_admin).
type AssignableRole = 'member' | 'admin' | 'it_admin'

const ROLE_OPTIONS: Array<{ value: AssignableRole; label: string }> = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
  { value: 'it_admin', label: 'IT Admin' },
]

interface MemberRoleSelectProps {
  userId: string
  currentRole: EnterpriseRole | null
  triggerClassName?: string
}

const MemberRoleSelect = ({ userId, currentRole, triggerClassName }: MemberRoleSelectProps) => {
  const callerUserId = useSelector(selectUser).userId
  const queryClient = useQueryClient()
  const { trackEvent } = useAnalytics()
  const { updateToast } = useToast()

  const isSelf = !!callerUserId && callerUserId === userId

  const roleMutation = useMutation({
    mutationFn: (nextRole: AssignableRole) => updateMemberRole(userId, { role: nextRole }),
    onSuccess: (_, nextRole) => {
      // Prefix invalidation matches both `['team-members', 'table', ...]` and
      // `['team-members', 'detail', ...]` so the row/header both refresh.
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      // Enterprise-role change flips every team-membership's
      // is_effective_team_admin via the OR-with-enterprise-role rule —
      // refresh the CC-wide snapshot the Create-Department dialog reads.
      queryClient.invalidateQueries({ queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY] })
      trackEvent?.(EventName.RoleChanged, { user_id: userId, new_role: nextRole })
      updateToast('Role updated', 'success')
    },
    onError: (error: unknown) => {
      // Backend can refuse the change (e.g. self-downgrade-to-Member, or removing
      // the last billable user when converting yourself to IT Admin). Surface the
      // server-side `detail` so the admin sees the actual reason.
      const message = isApiError(error) ? error.message : 'Failed to update role'
      updateToast(message, 'error')
    },
  })

  const handleChange = (value: string) => {
    if (value === currentRole) return
    // Self-downgrade to member is forbidden server-side (403). We hide that
    // option in the picker when viewing yourself; this guard catches the
    // (impossible) case where the value somehow leaks through.
    if (isSelf && value === 'member') return
    roleMutation.mutate(value as AssignableRole)
  }

  // Superadmin / null / any non-assignable value falls through to the
  // placeholder — the dropdown only ever lets an admin pick one of the 3
  // assignable roles. When the current value is unassignable but known
  // (e.g. superadmin), seed the placeholder with its label so the trigger
  // still shows the role instead of the literal "Role" hint.
  const selectValue = currentRole && ROLE_OPTIONS.some((opt) => opt.value === currentRole) ? currentRole : undefined
  const triggerLabel = !selectValue && currentRole ? (ROLE_LABELS[currentRole] ?? 'Role') : 'Role'

  return (
    <Select value={selectValue} onValueChange={handleChange} disabled={roleMutation.isPending}>
      <SelectTrigger className={triggerClassName ?? 'min-w-[120px]'}>
        <SelectValue placeholder={triggerLabel} />
      </SelectTrigger>
      <SelectContent>
        {ROLE_OPTIONS.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            // Hide "Member" when viewing self — backend would 403 the
            // self-downgrade anyway, but suppressing the option avoids a
            // confusing error.
            disabled={isSelf && opt.value === 'member'}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export default MemberRoleSelect
